/* eslint-disable max-lines */
import { useAtom, useSetAtom } from "jotai";
import { Loader } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  artboardBackgroundAtom,
  mobileSizePlacementOpenAtom,
  useArtworkForm,
  useEnhancedImageSource
} from "@design-lab/entities/artwork";
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  pinsAtom,
  useCancelPinningSession,
  useCompletePinningSession,
  useFetchPins
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinningModeCancelled,
  trackDesignLabPinningSessionCompleted,
  trackDesignLabPinningSessionCompletionFailed
} from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@design-lab/shared/ui/tooltip";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { ArtboardBase } from "../ArtboardBase";

// PinningTooltip using the shared Tooltip component
function PinningTooltip({ message, onClose }: { message: string; onClose: () => void }) {
  const [isOpen, setIsOpen] = useState(true);

  if (!isOpen) return null;

  return (
    <TooltipProvider>
      <Tooltip open={true}>
        <TooltipTrigger asChild data-testid="pinning-tooltip-trigger">
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "20%",
              transform: "translate(-50%, -50%)",
              width: 1,
              height: 1,
              pointerEvents: "none"
            }}
          />
        </TooltipTrigger>
        <TooltipContent
          data-testid="pinning-artboard-tooltip"
          side="top"
          className="max-w-[260px] border-blue-600 bg-blue-600 text-center text-white"
          showArrow={true}
          arrowClassName="fill-blue-600 bg-blue-600 text-blue-600"
          onClose={() => {
            setIsOpen(false);
            onClose();
          }}
        >
          {message}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function PinningArtboard() {
  const { composition, placement } = useArtboard();
  const { form } = useArtworkForm();
  const side = placement === "back" ? "back" : "front";
  const { source } = useEnhancedImageSource({ side });

  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const [isMobileSizePlacementOpen, setIsMobileSizePlacementOpen] = useAtom(
    mobileSizePlacementOpenAtom
  );
  const artboardRef = useRef<HTMLDivElement>(null);

  const setPinningSessions = useSetAtom(pinningSessionsAtom);
  const [currentPinningSession, setCurrentPinningSession] = useAtom(currentPinningSessionAtom);
  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId] = useAtom(activePinIdAtom);
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [tooltipMode, setTooltipMode] = useState<"initial" | "add-next" | null>("initial");
  const { mutate: completeSession, isPending: isCompleting } = useCompletePinningSession();
  const { mutate: cancelSession, isPending: isCancelling } = useCancelPinningSession();

  // Get current session info
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentSessionId = currentPinningSession?.sessionId;

  // Check if there's at least one completed pin
  const hasCompletedPin = pins.some(pin => pin.state === "ready");
  const readyPinsCount = pins.filter(pin => pin.state === "ready").length;

  // Derive active pin and whether user can add new pins
  const activePin = pins.find(p => p.id === activePinId);
  const canAddNewPin =
    !activePin || activePin.state === "ready" || activePin.state === "unsupported";

  // Manage tooltip visibility based on pin states
  useEffect(() => {
    // Hide initial tooltip when first pin is added
    if (pins.length > 0 && tooltipMode === "initial") {
      setTooltipMode(null);
      return;
    }

    // Hide tooltip when user is working on an unfinished pin
    const hasActiveUnfinishedPin = pins.some(
      p => p.state === "draft" || p.state === "processing" || p.state === "needs_clarification"
    );
    if (hasActiveUnfinishedPin) {
      setTooltipMode(null);
      return;
    }

    // Show "add next pin" tooltip when a pin is completed and user can add more
    if (hasCompletedPin && canAddNewPin) {
      setTooltipMode("add-next");
    }
  }, [pins, hasCompletedPin, canAddNewPin, tooltipMode]);

  // Hydration: fetch existing pins on mount
  const { data: sessionData, isLoading: isLoadingPins } = useFetchPins(currentSessionId ?? null);

  useEffect(() => {
    if (!sessionData) return;

    // Handle both array response or { pins: [...] } response
    const pinsData = Array.isArray(sessionData) ? sessionData : (sessionData as any)?.pins;

    if (Array.isArray(pinsData)) {
      setPins(
        pinsData.map((p: any) => {
          const messages = Array.isArray(p.messages)
            ? p.messages.map((m: any) => ({
                id: m.id || "",
                role: m.role || "user",
                content: m.content || "",
                timestamp: m.timestamp || Date.now(),
                ...(m.options ? { options: m.options } : {}),
                ...(m.questionType ? { questionType: m.questionType } : {})
              }))
            : [];

          // If no messages but we have message field, create a synthetic AI message
          if (messages.length === 0 && p.message) {
            messages.push({
              id: `msg-${p.pin_id}`,
              role: "ai",
              content: p.message,
              timestamp: Date.now()
            });
          }

          // Map API status to internal state
          const state = p.status || p.state || "draft";

          return {
            id: p.pin_id || p.id || "",
            coordinates: p.coordinates || { x: 0, y: 0 },
            state,
            instruction: p.instruction || p.summary,
            messages
          };
        })
      );
    }
  }, [sessionData, setPins]);

  const surface = composition?.surfaces[placement];
  const frontSurface = composition?.surfaces?.front || composition?.surfaces?.frontLeftChest;
  const referenceBoundingBox =
    composition?.surfaces?.front?.boundingBox || composition?.surfaces?.frontLeftChest?.boundingBox;

  // Get all available surfaces for the current side
  const allSurfaces = [
    ...(composition?.surfaces?.front
      ? [{ surface: composition.surfaces.front, placement: "front" as const }]
      : []),
    ...(composition?.surfaces?.frontLeftChest
      ? [{ surface: composition.surfaces.frontLeftChest, placement: "frontLeftChest" as const }]
      : []),
    ...(composition?.surfaces?.back
      ? [{ surface: composition.surfaces.back, placement: "back" as const }]
      : [])
  ].filter(s => (placement === "back" ? s.placement === "back" : s.placement !== "back"));

  const canvasProps = {
    surface: surface || null,
    referenceBoundingBox,
    placement,
    artworkImage: source.enhancedImage?.image,
    frontSurface,
    allSurfaces,
    labType: "design" as const,
    disablePanning: false
  };

  const controls = {
    showSideSwitcher: false,
    showZoom: true,
    showBackgroundToggle: true,
    showPickAModel: true,
    showToolbar: false,
    showProductBrowser: false,
    showRemoveBackground: false,
    showSizePlacement: false
  };

  return (
    <div className="relative h-full flex-1" data-testid="pinning-artboard">
      <div
        ref={artboardRef}
        className="h-full w-full overflow-hidden rounded-xl"
        data-testid="pinning-artboard-container"
      >
        <ArtboardBase
          readonly={true} // Forces unselectable and no editing controls
          canvasProps={canvasProps}
          controls={controls}
          backgroundType={backgroundType}
          onBackgroundChange={setBackgroundType}
          currentSide={side}
          zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
          // Minimal props required for rendering
          loadingState={{ isLoading: false, imagePreview: undefined }}
          frontLoadingState={{ isLoading: false, imagePreview: undefined }}
          backLoadingState={{ isLoading: false, imagePreview: undefined }}
          mobileSizePlacementOpen={isMobileSizePlacementOpen}
          onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
        />
      </div>
      {tooltipMode && (
        <PinningTooltip
          message={
            tooltipMode === "initial"
              ? "Click anywhere on the image to drop a pin and add instructions for our designers."
              : "Click anywhere on the image to add another pin"
          }
          onClose={() => setTooltipMode(null)}
        />
      )}

      {/* Loading overlay while fetching pins */}
      {isLoadingPins && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
          data-testid="artboard-loading-overlay"
        >
          <div className="flex flex-col items-center gap-2">
            <Loader className="h-8 w-8 animate-spin text-blue-700" />
            <span className="text-sm font-medium text-gray-700">Loading pins...</span>
          </div>
        </div>
      )}

      {/* Action buttons overlay */}
      <div
        className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-end rounded-b-xl bg-white p-2 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
        data-testid="pinning-artboard-actions"
      >
        <Button
          variant="link"
          className="text-blue-700 no-underline hover:text-blue-800 hover:no-underline"
          onPress={() => {
            if (currentArtworkId && currentSessionId) {
              cancelSession(currentSessionId, {
                onSuccess: () => {
                  // Track pinning mode cancelled
                  trackDesignLabPinningModeCancelled({
                    artworkId: currentArtworkId,
                    sessionId: currentSessionId,
                    pinsCount: pins.length,
                    readyPinsCount
                  });
                  // Remove session from sessions map
                  setPinningSessions(prev => {
                    const next = { ...prev };
                    delete next[currentArtworkId];
                    return next;
                  });
                  setPins([]);
                  setCurrentPinningSession(null);
                  setPinningMode(false);
                }
              });
              return;
            }
            setPins([]);
            setCurrentPinningSession(null);
            setPinningMode(false);
          }}
          data-testid="pinning-cancel"
          isDisabled={isCompleting || isCancelling}
        >
          Cancel
        </Button>
        <Button
          className="bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
          onPress={() => {
            if (!currentSessionId || !currentArtworkId) {
              // No valid session, just exit without completing
              setPinningMode(false);
              return;
            }

            completeSession(currentSessionId, {
              onSuccess: data => {
                // Track session completed
                trackDesignLabPinningSessionCompleted({
                  artworkId: currentArtworkId,
                  sessionId: currentSessionId,
                  pinsCount: pins.length,
                  readyPinsCount,
                  summary: data.summary || ""
                });

                // Mark session as completed with summary
                setPinningSessions(prev => ({
                  ...prev,
                  [currentArtworkId]: {
                    sessionId: currentSessionId,
                    state: "completed",
                    summary: data.summary ?? null
                  }
                }));
                // TEMP: Set the summary as the enhancement request note and enable design services
                if (data.summary) {
                  form.setValue(`info.${side}.enhancementRequestNote`, data.summary);
                  form.setValue(`info.${side}.jiffyDesignServices`, true);
                }
                // Clear current pinning session and exit pinning mode
                setCurrentPinningSession(null);
                setPinningMode(false);
              },
              onError: (error: Error) => {
                // Track session completion failed
                trackDesignLabPinningSessionCompletionFailed({
                  artworkId: currentArtworkId,
                  sessionId: currentSessionId,
                  errorMessage: error?.message || "Unknown error"
                });
                // On error, still exit pinning mode but don't mark as completed
                setPinningMode(false);
              }
            });
          }}
          data-testid="pinning-done"
          isDisabled={isCompleting || !hasCompletedPin || isCancelling}
        >
          {isCompleting ? "Completing..." : "Done"}
        </Button>
      </div>
    </div>
  );
}
